"use client";

import { useEffect, useState, type ChangeEvent } from "react";
import { Button, Input } from "@heroui/react";
import toast from "react-hot-toast";
import { api } from "@/lib/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { useTranslations } from "../../../../i18n";

export default function ProfileAccountTab({ isActive }: { isActive: boolean }) {
  const t = useTranslations("profile.accountTab");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    language: "fr",
  });

  useEffect(() => {
    if (!isActive) return;

    const loadProfile = async () => {
      try {
        setLoading(true);
        const data = await api("/api/widget/profile/me");
        setFormData({
          name: data.user.name || "",
          email: data.user.email || "",
          language: data.user.language || "fr",
        });
      } catch (error) {
        console.error("Failed to load profile", error);
        toast.error(t("toasts.loadError"));
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [isActive]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api("/api/widget/profile/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          language: formData.language,
        }),
      });
      toast.success(t("toasts.updateSuccess"));
    } catch (error) {
      console.error("Failed to update profile", error);
      toast.error(t("toasts.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api("/api/widget/profile/me", {
        method: "DELETE",
      });
      setDeleteOpen(false);
      setDeleteSuccess(true);
    } catch (error) {
      console.error("Failed to delete user", error);
      toast.error(t("toasts.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title={t("deleteDialog.title")}
        message={t("deleteDialog.message")}
        confirmLabel={t("deleteDialog.confirm")}
        cancelLabel={t("deleteDialog.cancel")}
        isDeleting={deleting}
      />

      <Input
        label={t("fields.name")}
        name="name"
        value={formData.name}
        onChange={handleChange}
        isDisabled={loading || deleteSuccess}
      />

      <div>
        <Input
          label={t("fields.email")}
          name="email"
          type="email"
          value={formData.email}
          isDisabled
        />
        {/* <p className="text-xs text-gray-500 mt-1">
          if you wanna change your eamil please contact us
        </p> */}
      </div>

      {/* <Select
        label="Language"
        name="language"
        value={formData.language}
        onChange={handleChange}
        isDisabled={loading || deleteSuccess}
      >
        <SelectItem key="fr">French</SelectItem>
        <SelectItem key="de">German</SelectItem>
        <SelectItem key="en">English</SelectItem>
      </Select> */}

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button
          color="primary"
          onPress={handleSave}
          isLoading={saving}
          isDisabled={loading || deleteSuccess}
        >
          {t("actions.save")}
        </Button>

        <Button
          color="danger"
          variant="flat"
          onPress={() => setDeleteOpen(true)}
          isDisabled={loading || deleteSuccess}
        >
          {t("actions.deleteAccount")}
        </Button>
      </div>

      {deleteSuccess && (
        <p className="text-sm text-success">{t("deleteSuccess")}</p>
      )}
    </div>
  );
}
